import React, { useState } from "react";
import { useGetPredictionMarketTrendingEvents } from "@/api/predictionMarketApi";
import { PredictionMarketTrendingEventItem, Theme } from "./types";
import { useUserContext } from "@/contexts/userContext";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  TrendingUp,
  Activity,
  Zap,
  CheckCircle,
  HelpCircle,
  ArrowRight,
  TrendingDown,
  Info,
  DollarSign,
  Briefcase,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Loader from "../Loader";
import { Pagination } from "@/helpers/helperFunctions";

interface PredictionMarketTrendingWidgetProps {
  theme?: Theme;
  width?: string | number;
  className?: string;
}

export const PredictionMarketTrendingWidget: React.FC<
  PredictionMarketTrendingWidgetProps
> = ({ width = "100%", className = "" }) => {
  const { t } = useTranslation("common-page");
  const { user } = useUserContext();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [activeTabMap, setActiveTabMap] = useState<
    Record<string, "markets" | "analytics" | "summary">
  >({});
  const [page, setPage] = useState(1);

  // Fetch trending events using the query hook
  const { data, isLoading, error } = useGetPredictionMarketTrendingEvents(
    user?.user_id || "SWDZyl",
    page,
    10,
  );

  const events: PredictionMarketTrendingEventItem[] = data?.items || [];

  // Filter events based on search query and category
  const filteredEvents = events.filter((item) => {
    const matchesSearch =
      item.event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.event.sub_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.event.event_ticker.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "All" ||
      item.event.category.toLowerCase() === selectedCategory.toLowerCase();

    return matchesSearch && matchesCategory;
  });

  // Extract all unique categories
  const categories = [
    "All",
    ...Array.from(new Set(events.map((item) => item.event.category))),
  ];

  const getActiveTab = (eventId: string) => {
    return activeTabMap[eventId] || "markets";
  };

  const setActiveTab = (
    eventId: string,
    tab: "markets" | "analytics" | "summary",
  ) => {
    setActiveTabMap((prev) => ({ ...prev, [eventId]: tab }));
  };

  const handleSimulateTrade = (
    eventTitle: string,
    marketName: string,
    side: "YES" | "NO",
    price: string,
  ) => {
    toast({
      title: "Initiating Trading Strategy",
      description: `Analyzing ${side} position for "${marketName}" in "${eventTitle}" at $${price}...`,
    });

    // Simulate strategy builder redirection
    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.open(`https://app.trylevel2.com/create-new-account`, "_blank");
      }
    }, 1500);
  };

  return (
    <>
      <Card className={className} style={{ width }}>
        <CardHeader className="flex !p-3 px-6 justify-between flex-row items-center w-full ">
          <CardTitle className="text-xl flex gap-0 items-start text-nowrap flex-col">
            Trending Predictions
          </CardTitle>
        </CardHeader>

        <p className="text-sm !px-3 text-muted-foreground font-medium">
          {
            "Real-time predictive insights & quantitative analytics on active event markets."
          }
        </p>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 px-6 pt-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all ${
                selectedCategory === category
                  ? "bg-blue-600 text-white "
                  : "bg-[#111726]/50 text-white/60 hover:text-white hover:bg-[#111726] border border-white/5"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <CardContent className="p-3 pt-0 w-full h-[500px] overflow-auto">
          {/* Loading State */}
          {isLoading && (
            <div className="w-full h-full flex justify-center items-center">
              <Loader text="Loading"></Loader>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <HelpCircle className="w-12 h-12 text-red-500/80 mb-2" />
              <p className="text-white/80 font-semibold">
                Failed to fetch trending events
              </p>
              <p className="text-white/40 text-xs mt-1">
                Please try again later.
              </p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredEvents.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Activity className="w-12 h-12 text-white/10 mb-2" />
              <p className="text-white/40 text-sm">
                No trending prediction events match your search.
              </p>
            </div>
          )}

          {/* Events Grid */}
          <div className="grid grid-cols-1 gap-6 mt-4">
            <AnimatePresence>
              {!isLoading &&
                filteredEvents.map((item, idx) => {
                  const eventId = item.event.event_ticker;
                  const tab = getActiveTab(eventId);
                  const signalScore =
                    item.analytics?.summary?.signal_score || 0;
                  const image =
                    item.metadata?.image_url || "/images/logos/Logo_Dark.png";

                  // Map colors for UI visualization
                  const getScoreColor = (score: number) => {
                    if (score >= 60) return "text-[--marketplace-green] ";
                    if (score >= 40) return "text-amber-400";
                    return "text-[--marketplace-red] ";
                  };

                  return (
                    <motion.div
                      key={eventId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                    >
                      <Card className="bg-[#0D1220]/80 border-white/5 overflow-hidden shadow-xl hover:border-white/10 transition-colors group">
                        {/* Event Banner & Basic Info */}
                        <div className="relative p-6 flex flex-col md:flex-row gap-6 items-start justify-between border-b border-white/5 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent">
                          <div className="flex gap-4 items-start">
                            {image && (
                              <div className="w-16 h-16 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex-shrink-0 flex items-center justify-center">
                                <img
                                  src={image}
                                  alt={item.event.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-350"
                                  onError={(e) => {
                                    // Fallback logic
                                    (e.target as any).src =
                                      "https://kalshi-public-docs.s3.amazonaws.com/series-images-webp/KXSWIFTKELCEWEDDINGLOCATION.webp";
                                  }}
                                />
                              </div>
                            )}
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge
                                  variant={"secondary"}
                                  className="px-2 py-0.5 text-[10px]"
                                >
                                  {item.event.category}
                                </Badge>
                              </div>
                              <h3 className="text-lg font-bold text-white mt-1   leading-tight">
                                {item.event.title}
                              </h3>
                              <p className="text-white/40 text-xs mt-0.5">
                                {item.event.sub_title}
                              </p>
                            </div>
                          </div>

                          {/* Signal Score Badge */}
                          <div className="flex items-center gap-4 self-stretch md:self-auto justify-between md:justify-end  pt-4 md:pt-0">
                            <div
                              className={`px-4 py-2 rounded-xl  flex flex-col items-center ${getScoreColor(signalScore)}`}
                            >
                              <span className="text-[10px] uppercase font-bold tracking-wider opacity-60">
                                Signal Score
                              </span>
                              <span className="text-xl font-black">
                                {signalScore.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="flex border-b border-white/5 px-6 bg-[#0a0f1c]/80">
                          {(["markets", "analytics", "summary"] as const).map(
                            (t) => (
                              <button
                                key={t}
                                onClick={() => setActiveTab(eventId, t)}
                                className={`py-3 px-4 text-xs font-bold uppercase tracking-wider relative transition-colors ${
                                  tab === t
                                    ? "text-blue-400"
                                    : "text-white/40 hover:text-white/60"
                                }`}
                              >
                                {t === "markets"
                                  ? "Markets & Trade"
                                  : t === "analytics"
                                    ? "Quantitative Signals"
                                    : "Summary Metrics"}
                                {tab === t && (
                                  <motion.div
                                    layoutId={`active-tab-${eventId}`}
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"
                                  />
                                )}
                              </button>
                            ),
                          )}
                        </div>

                        {/* Tab Content */}
                        <CardContent className="p-6">
                          {/* Markets Tab */}
                          {tab === "markets" && (
                            <div className="flex flex-col gap-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {item.markets.slice(0, 4).map((market) => {
                                  const prob =
                                    parseFloat(market.last_price_dollars) * 100;
                                  return (
                                    <div
                                      key={market.ticker}
                                      className="bg-[#111726]/40 border border-white/5 rounded-xl p-4 flex flex-col justify-between hover:border-white/10 transition-colors"
                                    >
                                      <div>
                                        <div className="flex justify-between items-start gap-2">
                                          <h4 className="font-bold text-white/90 text-sm">
                                            {market.readable_name ||
                                              market.subtitle}
                                          </h4>
                                          <span className="text-foreground text-sm font-black">
                                            {prob.toFixed(0)}%
                                          </span>
                                        </div>

                                        {/* Visual probability bar */}
                                        <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden mt-2">
                                          <div
                                            className="bg-[--accent-primary] h-full rounded-full"
                                            style={{ width: `${prob}%` }}
                                          />
                                        </div>

                                        {/* Details stats */}
                                        <div className="flex justify-between items-center text-[10px] text-white/40 mt-3 border-b border-white/5 pb-2">
                                          <span>
                                            24h Vol: $
                                            {parseFloat(
                                              market.volume_24h_fp || "0",
                                            ).toLocaleString(undefined, {
                                              maximumFractionDigits: 0,
                                            })}
                                          </span>
                                          <span>
                                            Open Interest:{" "}
                                            {parseFloat(
                                              market.open_interest_fp || "0",
                                            ).toLocaleString(undefined, {
                                              maximumFractionDigits: 0,
                                            })}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Bid Ask / Action */}
                                      <div className="grid grid-cols-2 gap-2 mt-3">
                                        <Button
                                          onClick={() =>
                                            handleSimulateTrade(
                                              item.event.title,
                                              market.readable_name ||
                                                market.subtitle,
                                              "YES",
                                              market.yes_ask_dollars,
                                            )
                                          }
                                          variant="outline"
                                          className="h-8  text-[--marketplace-green] border-none text-xs flex justify-center px-2 "
                                        >
                                          <span>YES</span>
                                          <span>${market.yes_ask_dollars}</span>
                                        </Button>
                                        <Button
                                          onClick={() =>
                                            handleSimulateTrade(
                                              item.event.title,
                                              market.readable_name ||
                                                market.subtitle,
                                              "NO",
                                              market.no_ask_dollars,
                                            )
                                          }
                                          variant="outline"
                                          className="h-8  text-[--marketplace-red] border-none bg-transparent  text-xs flex justify-center px-2 "
                                        >
                                          <span>NO</span>
                                          <span>${market.no_ask_dollars}</span>
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* View Kalshi details link */}
                              {item.event.event_url && (
                                <a
                                  href={item.event.event_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1.5 self-start mt-2 transition-colors"
                                >
                                  View official market rules & details{" "}
                                  <ArrowRight className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          )}

                          {/* Analytics Tab */}
                          {tab === "analytics" && (
                            <div className="flex flex-col gap-6">
                              {item.analytics?.event_details &&
                              item.analytics.event_details.length > 0 ? (
                                item.analytics.event_details.map(
                                  (indicator, indIdx) => (
                                    <div
                                      key={indicator.strategy_id || indIdx}
                                      className="bg-[#111726]/20 border border-white/5 rounded-xl p-4 relative overflow-hidden"
                                    >
                                      <div className="flex justify-between items-start gap-4 mb-2">
                                        <div className="flex items-center gap-2">
                                          <Zap className="w-4 h-4 text-yellow-400" />
                                          <h4 className="font-bold text-white text-sm">
                                            {indicator.title}
                                          </h4>
                                        </div>
                                      </div>
                                      <p className="text-white/50 text-[11px] leading-relaxed mb-4">
                                        {indicator.description}
                                      </p>

                                      {/* Table or Grid of Signals */}
                                      <div className="grid grid-cols-1 gap-2">
                                        {indicator.data &&
                                          indicator.data
                                            .slice(0, 3)
                                            .map((sigData, sigIdx) => {
                                              const signalStrength =
                                                sigData.signal_strength || 0;
                                              const isBuy =
                                                sigData.decision === "buy" ||
                                                sigData.direction === "BUY";
                                              const isShort =
                                                sigData.decision === "sell" ||
                                                sigData.direction === "SHORT";

                                              return (
                                                <div
                                                  key={sigIdx}
                                                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-[#111726]/40 rounded-lg p-3 border border-white/5 gap-2"
                                                >
                                                  <div className="flex items-center gap-2">
                                                    <div
                                                      className={`w-2.5 h-2.5 rounded-full ${isBuy ? "bg-emerald-500 animate-pulse" : isShort ? "bg-red-500 animate-pulse" : "bg-white/20"}`}
                                                    />
                                                    <span className="font-semibold text-xs text-white/90">
                                                      {sigData.name ||
                                                        sigData.readable_ticker}
                                                    </span>
                                                  </div>

                                                  <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                                                    <div className="flex flex-col items-end">
                                                      <span
                                                        className={`text-xs font-bold uppercase ${isBuy ? "text-emerald-400" : isShort ? "text-red-400" : "text-white/40"}`}
                                                      >
                                                        {sigData.decision ||
                                                          sigData.direction ||
                                                          "HOLD"}
                                                      </span>
                                                      <span className="text-[10px] text-white/40">
                                                        $
                                                        {sigData.price?.toFixed(
                                                          2,
                                                        )}
                                                      </span>
                                                    </div>

                                                    {/* Signal strength bar */}
                                                    <div className="flex items-center gap-1.5">
                                                      <div className="w-16 bg-white/5 h-1.5 rounded-full overflow-hidden">
                                                        <div
                                                          className={`h-full rounded-full ${isBuy ? "bg-emerald-500" : isShort ? "bg-red-500" : "bg-white/20"}`}
                                                          style={{
                                                            width: `${signalStrength}%`,
                                                          }}
                                                        />
                                                      </div>
                                                      <span className="text-[10px] font-bold text-white/60 w-8">
                                                        {signalStrength}%
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                      </div>
                                    </div>
                                  ),
                                )
                              ) : (
                                <div className="text-center py-6 text-white/40 text-xs">
                                  No quantitative indicator alerts for this
                                  event series.
                                </div>
                              )}
                            </div>
                          )}

                          {/* Summary Metrics Tab */}
                          {tab === "summary" && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {/* Favorite */}
                              <div className="bg-[#111726]/40 border border-white/5 rounded-xl p-4 flex flex-col justify-between">
                                <span className="text-[10px] text-white/40 uppercase font-bold tracking-wider flex items-center gap-1">
                                  <CheckCircle className="w-3.5 h-3.5 text-blue-400" />{" "}
                                  Market Favorite
                                </span>
                                <div className="mt-3">
                                  <div className="text-white font-bold text-sm truncate">
                                    {item.analytics?.summary?.favorite || "N/A"}
                                  </div>
                                  <div className="text-blue-400 text-xs font-bold mt-0.5">
                                    {item.analytics?.summary
                                      ?.favorite_prob_pct || 0}
                                    % Implied
                                  </div>
                                </div>
                              </div>

                              {/* Concentration */}
                              <div className="bg-[#111726]/40 border border-white/5 rounded-xl p-4 flex flex-col justify-between">
                                <span className="text-[10px] text-white/40 uppercase font-bold tracking-wider flex items-center gap-1">
                                  <Activity className="w-3.5 h-3.5 text-indigo-400" />{" "}
                                  Volume Shock
                                </span>
                                <div className="mt-3">
                                  <div className="text-white font-bold text-lg">
                                    {(
                                      item.analytics?.summary?.volume_shock || 0
                                    ).toFixed(2)}
                                    x
                                  </div>
                                  <div className="text-white/40 text-[10px] mt-0.5">
                                    24h Vol / Open Interest
                                  </div>
                                </div>
                              </div>

                              {/* Total Volume */}
                              <div className="bg-[#111726]/40 border border-white/5 rounded-xl p-4 flex flex-col justify-between">
                                <span className="text-[10px] text-white/40 uppercase font-bold tracking-wider flex items-center gap-1">
                                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />{" "}
                                  Lifetime Volume
                                </span>
                                <div className="mt-3">
                                  <div className="text-white font-bold text-lg">
                                    $
                                    {(
                                      item.analytics?.summary?.total_volume || 0
                                    ).toLocaleString(undefined, {
                                      maximumFractionDigits: 0,
                                    })}
                                  </div>
                                  <div className="text-emerald-400 text-[10px] font-bold mt-0.5">
                                    +$
                                    {(
                                      item.analytics?.summary
                                        ?.total_volume_24h || 0
                                    ).toLocaleString(undefined, {
                                      maximumFractionDigits: 0,
                                    })}{" "}
                                    24h
                                  </div>
                                </div>
                              </div>

                              {/* Active Competitors */}
                              <div className="bg-[#111726]/40 border border-white/5 rounded-xl p-4 flex flex-col justify-between">
                                <span className="text-[10px] text-white/40 uppercase font-bold tracking-wider flex items-center gap-1">
                                  <Briefcase className="w-3.5 h-3.5 text-purple-400" />{" "}
                                  Contenders
                                </span>
                                <div className="mt-3">
                                  <div className="text-white font-bold text-lg">
                                    {item.analytics?.summary
                                      ?.effective_competitors ||
                                      item.markets.length}{" "}
                                    / {item.markets.length}
                                  </div>
                                  <div className="text-white/40 text-[10px] mt-0.5">
                                    Effective / Total Markets
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
            </AnimatePresence>
          </div>

        </CardContent>
        {/* Pagination controls */}
        {!isLoading && !error && events.length > 0 && (
          <div className="p-4 border-t border-white/5 bg-[#0a0f1c]/50 flex justify-end">
            <Pagination
              totalCount={data?.total || 30}
              currentPage={page}
              changePage={setPage}
              limit={10}
              alwaysVisible={true}
            />
          </div>
        )}
      </Card>
    </>
  );
};
export default PredictionMarketTrendingWidget;
